import React, { PureComponent } from 'react';
import Translator from 'translator';
import PropTypes from 'prop-types';
import {
    withStyles,
    CircularProgress,
    Divider,
    InputAdornment,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    Button,
    Grid,
    Tooltip
} from '@material-ui/core';
import { Visibility, VisibilityOff, Refresh } from '@material-ui/icons';
import { FHETextField, FHEReactSelect, OneWifiItem } from 'data/components';
import { WifiIcon } from 'data/common/iconsvg';
import memoizeOne from 'memoize-one';

class FHEWifiForm extends PureComponent {
    static propTypes = {
        classes: PropTypes.object.isRequired,
        theme: PropTypes.object.isRequired,
        getWifiList: PropTypes.func.isRequired,
        selectWifi: PropTypes.func.isRequired,
        handleChange: PropTypes.func.isRequired,
        wifiList: PropTypes.array.isRequired,
        ssid: PropTypes.string,
        password: PropTypes.string,
        encryption: PropTypes.string,
        encryption_type: PropTypes.string
    };

    static defaultProps = {
        ssid: '',
        password: '',
        encryption: '',
        encryption_type: ''
    };
    constructor(props) {
        super(props);
        this.state = {
            loadingWifi: false,
            errorWifi: false,
            showPassword: false,
            wifiSelection: false
        };
        this.delayToCheckMiddleWare = 500;
        this.encryptions = [
            { value: 'WPA2', label: Translator.translate('generic.products.addProduct.wifiForm.wpa2') },
            { value: 'WPA', label: Translator.translate('generic.products.addProduct.wifiForm.wpa') }
        ];
        this.encryptionTypes = [
            { value: 'AES', label: Translator.translate('generic.products.addProduct.wifiForm.aes') },
            { value: 'TKIP', label: Translator.translate('generic.products.addProduct.wifiForm.tkip') }
        ];
    }
    showWifiList = () => {
        this.getWifiList(true);
    };

    /* HELPERS */
    getBetterSignalLevelWifi = (list, property) => {
        let val,
            result = [],
            ssidArray = [];
        for (let i = 0; i < list.length; i++) {
            val = list[i][property];
            if (!ssidArray.includes(val)) {
                result.push(list[i]);
                ssidArray.push(list[i].ssid);
            }
        }
        return result;
    };

    getWifiList = (wifiSelection = false) => {
        const { getWifiList } = this.props;
        this.setState({
            loadingWifi: true,
            errorWifi: null,
            wifiSelection
        });
        getWifiList()
            .then(() => {
                this.setState({
                    loadingWifi: false,
                    errorWifi: null
                });
            })
            .catch(error => {
                let errorString = Translator.translateWithFallback(
                    `socket.error.${error.message ? error.message : error}`,
                    `socket.error.failedGetWifiList`
                );
                this.setState({
                    loadingWifi: false,
                    errorWifi: errorString
                });
            });
    };

    handleClickShowPassword = () => {
        const { showPassword } = this.state;
        this.setState({
            showPassword: !showPassword
        });
    };
    selectWifi = wifi => {
        const { selectWifi } = this.props;
        selectWifi(wifi);
        this.setState({ wifiSelection: false });
    };
    renderWifiList = () => {
        const { error } = this.state;
        const { classes, theme, wifiList } = this.props;

        let wifiListArray = [];

        if (wifiList.length > 0) {
            wifiList.sort(function(a, b) {
                return b.signalLevel - a.signalLevel;
            });
            let groupedWifiList = this.getBetterSignalLevelWifi(wifiList, 'ssid');
            groupedWifiList.forEach(wifi => {
                wifiListArray.push(
                    <div key={wifi.bssid} onClick={() => this.selectWifi(wifi)} role="presentation">
                        <OneWifiItem wifi={wifi} />
                        <Divider />
                    </div>
                );
            });

            return wifiListArray;
        } else {
            return (
                <div className={classes.errorTextContainer}>
                    <div style={{ color: theme.palette.error.main }}>
                        {error ? error : Translator.translate('general.addProduct.error.noWifiFound')}
                    </div>
                </div>
            );
        }
    };
    getSelectedEncryptionType = memoizeOne(type => {
        return this.encryptionTypes.find(x => x.value === type);
    });

    getSelectedEncryption = memoizeOne(encryption => {
        return this.encryptions.find(x => x.value === encryption);
    });

    handleReactSelect = (event, options) => {
        const { handleChange } = this.props;
        handleChange({
            target: {
                name: options.name,
                value: event.value
            }
        });
    };

    render() {
        const { showPassword, wifiSelection, loadingWifi, errorWifi } = this.state;
        const { ssid, password, encryption, encryption_type, handleChange, theme, classes } = this.props;

        return (
            <form>
                <Grid container spacing={3}>
                    <Grid container item sm={12}>
                        <FHETextField
                            id="ssid"
                            value={!ssid || ssid === null ? '' : ssid}
                            label={Translator.translate('general.addProduct.wifiForm.ssid')}
                            name="ssid"
                            onChange={handleChange}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Tooltip title={Translator.translate('general.addProduct.wifiForm.selectWifi')} placement="top">
                                            <IconButton aria-label="Select Wifi" onClick={this.showWifiList}>
                                                <WifiIcon level={100} color={theme.palette.primary.main} size={30} />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                    <Grid container item sm={12}>
                        <FHETextField
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            value={!password || password === null ? '' : password}
                            label={Translator.translate('general.addProduct.wifiForm.password')}
                            name="password"
                            onChange={handleChange}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton aria-label="Toggle password visibility" onClick={this.handleClickShowPassword}>
                                            {showPassword ? <Visibility /> : <VisibilityOff />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                    <Grid container item sm={12} md={6}>
                        <FHEReactSelect
                            name="encryption"
                            label={Translator.translate('generic.products.addProduct.wifiForm.encryption')}
                            onChange={this.handleReactSelect}
                            value={this.getSelectedEncryption(encryption)}
                            choices={this.encryptions}
                        />
                    </Grid>
                    <Grid container item sm={12} md={6}>
                        <FHEReactSelect
                            name="encryption_type"
                            label={Translator.translate('generic.products.addProduct.wifiForm.encryption_type')}
                            onChange={this.handleReactSelect}
                            value={this.getSelectedEncryptionType(encryption_type)}
                            choices={this.encryptionTypes}
                        />
                    </Grid>
                </Grid>
                <Dialog open={wifiSelection} aria-labelledby="wifiSelectionDialog">
                    <div className={classes.titleWifiList}>{Translator.translate('general.addProduct.wifiListTitle')}</div>
                    <DialogContent>
                        {errorWifi ? <span style={{ color: theme.palette.error.main }}>{errorWifi}</span> : <span>{this.renderWifiList()}</span>}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => {
                                this.setState({
                                    wifiSelection: false
                                });
                            }}
                            color="primary"
                        >
                            {Translator.translate('general.close')}
                        </Button>
                        {loadingWifi ? (
                            <IconButton aria-label="LoadingWifiList">
                                <CircularProgress size={25} />
                            </IconButton>
                        ) : (
                            <Tooltip title={Translator.translate('general.refresh')} placement="top">
                                <IconButton aria-label="RefreshWifiList" onClick={this.getWifiList}>
                                    <Refresh />
                                </IconButton>
                            </Tooltip>
                        )}
                    </DialogActions>
                </Dialog>
            </form>
        );
    }
}

const styles = () => ({
    errorTextContainer: {
        width: '100%',
        textAlign: 'center',
        marginBottom: '10px',
        marginTop: '10px'
    },
    titleWifiList: {
        marginTop: 15,
        marginBottom: 15,
        fontSize: '1.2em',
        marginLeft: 15
    }
});

export default withStyles(styles, { withTheme: true })(FHEWifiForm);



// WEBPACK FOOTER //
// ./data/components/FHEWifiForm.js